import { getAwardShowLandingBySlug } from "./award-show-landings";
import { normalizeAwardShowImageUrl } from "./utils";
import type {
  BlogPostInput,
  BlogPostStatus,
  ValidatedBlogPost,
} from "./types/blog";

const BLOG_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const RESERVED_BLOG_SLUGS = new Set(["feed.xml", "rss", "feed"]);

export function slugifyBlogSlug(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function isValidBlogSlug(slug: string): boolean {
  if (!BLOG_SLUG_PATTERN.test(slug)) {
    return false;
  }
  if (RESERVED_BLOG_SLUGS.has(slug)) {
    return false;
  }
  return slug.length >= 2 && slug.length <= 120;
}

export function parseBlogRelatedShowSlugs(raw: string): string[] {
  const seen = new Set<string>();
  const slugs: string[] = [];

  for (const part of raw.split(",")) {
    const slug = part.trim();
    if (!slug || seen.has(slug)) {
      continue;
    }
    seen.add(slug);
    slugs.push(slug);
  }

  return slugs;
}

export type ValidateBlogPostResult =
  | { isValid: true; data: ValidatedBlogPost }
  | { isValid: false; error: string };

function isBlogPostStatus(value: string): value is BlogPostStatus {
  return value === "DRAFT" || value === "PUBLISHED";
}

export function validateBlogPostInput(
  input: BlogPostInput,
): ValidateBlogPostResult {
  const title = input.title.trim();
  if (!title) {
    return { isValid: false, error: "Title is required" };
  }

  const slug = slugifyBlogSlug(input.slug);
  if (!slug) {
    return { isValid: false, error: "Slug is required" };
  }
  if (!isValidBlogSlug(slug)) {
    return {
      isValid: false,
      error: "Slug must be lowercase letters, numbers, and hyphens",
    };
  }

  const description = input.description.trim();
  if (!description) {
    return { isValid: false, error: "Description is required" };
  }

  const body = input.body.trim();
  if (!body) {
    return { isValid: false, error: "Body is required" };
  }

  if (!isBlogPostStatus(input.status)) {
    return { isValid: false, error: "Status must be draft or published" };
  }

  const coverRaw = input.coverImageUrl.trim();
  let coverImageUrl: string | null = null;
  if (coverRaw) {
    coverImageUrl = normalizeAwardShowImageUrl(coverRaw);
    if (!coverImageUrl) {
      return { isValid: false, error: "Cover image must be a valid https URL" };
    }
  }

  const relatedShowSlugs = parseBlogRelatedShowSlugs(input.relatedShowSlugs);
  for (const showSlug of relatedShowSlugs) {
    if (!getAwardShowLandingBySlug(showSlug)) {
      return {
        isValid: false,
        error: `Unknown related award show slug: ${showSlug}`,
      };
    }
  }

  return {
    isValid: true,
    data: {
      title,
      slug,
      description,
      body,
      status: input.status,
      coverImageUrl,
      relatedShowSlugs,
    },
  };
}
